// Pure stroke → canvas rasterizer. Shared by useDrawing (paints the
// on-screen canvas the player sees) and scoring (paints an offscreen
// canvas for pixel-quantization). Keeping a single rasterizer here is
// load-bearing — divergence between the two would silently shift
// scoring numbers without any feature change.
import { hexToRgb } from "./colorUtils";

function resolveColor(stroke, palette) {
  if (stroke.tool === "eraser") return null;
  if (stroke.colorIndex != null) return palette[stroke.colorIndex];
  return stroke.color ?? "#000";
}

export function renderPenStroke(ctx, stroke, palette) {
  const color = resolveColor(stroke, palette);
  ctx.save();
  if (stroke.tool === "eraser") {
    ctx.globalCompositeOperation = "destination-out";
    ctx.strokeStyle = "#000";
    ctx.fillStyle = "#000";
  } else {
    ctx.globalCompositeOperation = "source-over";
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
  }
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  const pts = stroke.points;
  if (pts.length === 1) {
    const p = pts[0];
    const w = stroke.size * (p.pressure ?? 1);
    ctx.beginPath();
    ctx.arc(p.x, p.y, w / 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
    return;
  }

  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1];
    const b = pts[i];
    const wA = stroke.size * (a.pressure ?? 1);
    const wB = stroke.size * (b.pressure ?? 1);
    ctx.lineWidth = (wA + wB) / 2;
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
    ctx.stroke();
  }
  ctx.restore();
}

function renderFillStroke(ctx, stroke, palette) {
  const color = resolveColor(stroke, palette);
  const rgb = hexToRgb(color);
  const { x, y, w, h } = stroke.bbox;
  if (w <= 0 || h <= 0) return;
  const imgData = ctx.getImageData(x, y, w, h);
  const { data } = imgData;
  const mask = stroke.mask;
  for (let i = 0; i < mask.length; i++) {
    if (mask[i]) {
      const di = i * 4;
      data[di] = rgb.r;
      data[di + 1] = rgb.g;
      data[di + 2] = rgb.b;
      data[di + 3] = 255;
    }
  }
  ctx.putImageData(imgData, x, y);
}

export function renderStroke(ctx, stroke, palette) {
  if (stroke.tool === "fill") {
    renderFillStroke(ctx, stroke, palette);
  } else {
    renderPenStroke(ctx, stroke, palette);
  }
}

export function redraw(canvas, strokes, palette) {
  if (!canvas) return;
  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  for (const s of strokes) renderStroke(ctx, s, palette);
}
